"use client";

import Link from "next/link";
import { useState, useEffect } from "react";
import { NavItem } from "@/types";
import Image from "next/image";
import logoSvg from "@/assets/logo.svg";
import { useAppSelector } from "@/hooks/useAppSelector";

const navItems: NavItem[] = [
  { name: "Home", href: "/" },
  { name: "About", href: "/about" },
  { name: "Treatments", href: "/products" },
  { name: "Blogs", href: "/blog" },
  { name: "Services", href: "/services" },
  { name: "FAQs", href: "/faqs" },
  { name: "Contact", href: "/contact" },
];

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [_, setIsDrawerVisible] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { totalItems } = useAppSelector((state) => state.cart);

  useEffect(() => {
    if (isMenuOpen) {
      setIsDrawerVisible(true);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
      const timer = setTimeout(() => setIsDrawerVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`relative bg-white sticky top-0 z-50 transition-all duration-300 ${
        isScrolled ? "shadow-[0_4px_20px_-2px_rgba(0,0,0,0.08)]" : ""
      }`}
    >
      <div className="py-3.5 sm:py-4 px-4 sm:px-6 lg:px-20">
        <div className="max-w-[1440px] mx-auto">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link href="/" className="flex items-center group flex-shrink-0">
              <div className="relative">
                <Image
                  src="/images/logo/logo-02.png"
                  alt="UKWeightloss - Medical Weight Loss Specialists"
                  width={180}
                  height={45}
                  className="h-9 sm:h-11 lg:h-12 xl:h-14 w-auto transition-opacity duration-300 group-hover:opacity-80"
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8 xl:space-x-10">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className="relative text-gray-600 hover:text-[#7771B8] text-base xl:text-lg font-medium transition-colors duration-300 py-2"
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* Action Buttons */}
            <div className="flex items-center">
              {/* CTA Button - Desktop */}
              <Link
                href="/consultation/GUHMJBNGE33R7CJ"
                className="hidden lg:block"
              >
                <button className="bg-[#7771B8] hover:bg-[#6860a3] text-white font-semibold px-6 py-2.5 xl:px-7 xl:py-3 shadow-md shadow-[#7771B8]/20 hover:shadow-lg hover:shadow-[#7771B8]/25 transition-all duration-300 rounded-lg text-sm xl:text-base">
                  Start Consultation
                </button>
              </Link>

              {/* Mobile Actions */}
              <div className="flex items-center space-x-2 sm:space-x-3 lg:hidden">
                {/* Mobile CTA */}
                <Link href="/consultation/GUHMJBNGE33R7CJ">
                  <button className="bg-[#7771B8] hover:bg-[#6860a3] text-white font-semibold px-4 py-2.5 sm:px-5 sm:py-2.5 rounded-lg shadow-sm shadow-[#7771B8]/20 transition-all duration-300 text-xs sm:text-sm">
                    Get Started
                  </button>
                </Link>

                {/* Mobile Login Button */}
                <Link href="/account">
                  <button className="p-2.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center transition-colors duration-300">
                    <svg
                      className="h-5 w-5 text-gray-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </button>
                </Link>

                {/* Mobile Menu Button */}
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="p-2.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center transition-colors duration-300"
                >
                  <svg
                    className="h-5 w-5 text-gray-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    {isMenuOpen ? (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    ) : (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6h16M4 12h16M4 18h16"
                      />
                    )}
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 z-[100] lg:hidden transition-opacity duration-300"
              onClick={() => setIsMenuOpen(false)}
            />

            {/* Drawer */}
            <div className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-[0_4px_32px_rgba(0,0,0,0.12)] z-[110] lg:hidden">
              <div className="flex flex-col h-full">
                {/* Drawer Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                  <Link href="/" onClick={() => setIsMenuOpen(false)}>
                    <Image
                      src="/images/logo/logo-02.png"
                      alt="UKWeightloss - Medical Weight Loss Specialists"
                      width={180}
                      height={45}
                      className="h-9 w-auto"
                    />
                  </Link>
                  <button
                    onClick={() => setIsMenuOpen(false)}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-300"
                  >
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                {/* Drawer Navigation */}
                <div className="flex-1 px-4 py-6 overflow-y-auto">
                  <div className="space-y-1">
                    {navItems.map((item) => (
                      <Link
                        key={item.name}
                        href={item.href}
                        className="text-gray-600 hover:text-[#7771B8] hover:bg-gray-50 block px-4 py-3 text-base font-medium rounded-lg transition-colors duration-300"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>

                  {/* Drawer CTA */}
                  <div className="mt-8 pt-6 border-t border-gray-100">
                    <Link
                      href="/consultation/GUHMJBNGE33R7CJ"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <button className="w-full bg-[#7771B8] hover:bg-[#6860a3] text-white font-semibold py-3.5 rounded-lg shadow-md shadow-[#7771B8]/20 transition-all duration-300">
                        Start my journey
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </header>
  );
}
